import { z } from "zod";
import type { infer as zInfer } from "zod";
import { timestamp } from "@/hooks/firestore/models/timestamp";

const marketFilterEntry = z.object({
  marketType: z.string(),
  marketTypeId: z.string().optional(),
  priority: z.coerce.number().optional(),
});

// unioned, not discriminated - an unrecognised render style still parses via
// the catch-all, instead of failing the whole doc
const marketFilterRenderStyle = z.union([
  z.looseObject({ type: z.literal("HEAD_TO_HEAD") }),
  z.looseObject({
    type: z.literal("COLUMN_GRID"),
    columnConfiguration: z.object({
      key: z.string(),
      values: z.array(z.string()),
    }),
  }),
  z.looseObject({
    type: z.literal("HORIZONTAL_SEGMENT"),
    groupBy: z.object({
      key: z.string(),
      value: z.string(),
    }),
  }),
  z.looseObject({ type: z.string() }),
]);

// a group of markets rendered together, keyed by group id under a filter
// category. Label is the section header - falls back to toMarketDisplayGroups
// when backend doesn't send one, not the category/pill label
const marketFilterSubgroup = z.object({
  label: z.string().optional(),
  priority: z.coerce.number().optional(),
  renderStyle: marketFilterRenderStyle.optional(),
  entries: z.record(z.string(), marketFilterEntry).optional(),
});

// a filter category (the pill's identity), keyed by category id in `filters`
const marketFilterCategory = z.object({
  label: z.string().optional(),
  priority: z.coerce.number().optional(),
  groups: z.record(z.string(), marketFilterSubgroup).optional(),
});

export const bettingMarketFilters = z.looseObject({
  // backend scheduling field - the client shows whatever config it is served
  activeFrom: timestamp.optional(),
  filters: z.record(z.string(), marketFilterCategory).optional(),
  // unfiltered/"All" view groups - a separate scope from filters' own groups,
  // not merged with them (a marketType can independently belong to both)
  groups: z.record(z.string(), marketFilterSubgroup).optional(),
});

export type BettingMarketFilters = zInfer<typeof bettingMarketFilters>;
export type MarketFilterRenderStyle = zInfer<typeof marketFilterRenderStyle>;

type ColumnGridRenderStyle = Extract<
  MarketFilterRenderStyle,
  { type: "COLUMN_GRID" }
>;
type HorizontalSegmentRenderStyle = Extract<
  MarketFilterRenderStyle,
  { type: "HORIZONTAL_SEGMENT" }
>;

// the catch-all variant that lets unrecognised types parse also stops TS
// narrowing on renderStyle.type === "..." - use these guards instead
export const isColumnGridRenderStyle = (
  renderStyle: MarketFilterRenderStyle | undefined,
): renderStyle is ColumnGridRenderStyle => renderStyle?.type === "COLUMN_GRID";

export const isHorizontalSegmentRenderStyle = (
  renderStyle: MarketFilterRenderStyle | undefined,
): renderStyle is HorizontalSegmentRenderStyle =>
  renderStyle?.type === "HORIZONTAL_SEGMENT";

// the shape check for the home/away column layout, shared by the dispatcher
// (Outcomes.tsx) and the header (MarketGroup.tsx) so they can't drift apart -
// still needs hasCleanHomeAwaySplit(outcomes) layered on at each call site,
// since that depends on the actual outcomes, not just the config
export const isHomeAwayColumnGrid = (
  renderStyle: MarketFilterRenderStyle | undefined,
): renderStyle is ColumnGridRenderStyle =>
  isColumnGridRenderStyle(renderStyle) &&
  renderStyle.columnConfiguration.key === "side" &&
  renderStyle.columnConfiguration.values.length === 2 &&
  renderStyle.columnConfiguration.values.includes("HOME") &&
  renderStyle.columnConfiguration.values.includes("AWAY");
